import React, { memo, useCallback } from "react";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import { InboxSlashIcon, InboxIcon } from "modules/icons";
import colors from "app/colors";
import { useMixpanel } from "modules/providers/mixpanel-provider";
import { MIXPANEL_EVENT_TYPE } from "modules/providers/mixpanel-provider/mixpanel-provider";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import { getDistributionGroupMention } from "modules/utils/Functions";
import {
  DistributionGroupFragment,
  EditDistributionGroupNotificationsMutation,
  EditDistributionGroupNotificationsMutationVariables,
} from "gql/graphql";
import { useAuth } from "modules/providers/auth-provider";
import { useMutation } from "@apollo/client";
import EDIT_DISTRIBUTION_GROUP_NOTIFICATIONS_MUTATION from "modules/contacts/networking/edit-distribution-group-notifications";

interface IProps {
  distributionGroup: DistributionGroupFragment;
}

const ToggleDistributionGroupInboxButton = ({ distributionGroup }: IProps) => {
  const { me } = useAuth();
  const { track } = useMixpanel();

  const isNotifications = me
    ? distributionGroup.notificationUserIdList.includes(me.id)
    : false;

  const [editDistributionGroupNotificationsMutationAPI] = useMutation<
    EditDistributionGroupNotificationsMutation,
    EditDistributionGroupNotificationsMutationVariables
  >(EDIT_DISTRIBUTION_GROUP_NOTIFICATIONS_MUTATION);

  const onToggleDistributionGroupNotificationsClicked = useCallback(() => {
    if (!me) return;

    editDistributionGroupNotificationsMutationAPI({
      variables: {
        id: distributionGroup.id,
        isNotifications: !isNotifications,
      },
      optimisticResponse: {
        editDistributionGroup: {
          __typename: "DistributionGroup",
          id: distributionGroup.id,
          notificationUserIdList: isNotifications
            ? distributionGroup.notificationUserIdList.filter(
                (id) => id !== me.id,
              )
            : [...distributionGroup.notificationUserIdList, me.id],
        },
      },
    });

    track(MIXPANEL_EVENT_TYPE.DISTRIBUTION_GROUP_NOTIFICATIONS_TOGGLED, {
      "Toggled Notifications On":
        !distributionGroup.notificationUserIdList.includes(me.id),
      "Group Name": getDistributionGroupMention(distributionGroup),
    });
  }, [
    distributionGroup,
    editDistributionGroupNotificationsMutationAPI,
    isNotifications,
    me,
    track,
  ]);

  if (isNotifications) {
    return (
      <SyncoTooltip label="Group messages sent to Threads Inbox">
        <CircularIconButton
          size="sm"
          variant="ghost"
          onClick={onToggleDistributionGroupNotificationsClicked}
          icon={<InboxIcon boxSize={5} fill={colors.blue} />}
          aria-label="Toggle Inbox"
        />
      </SyncoTooltip>
    );
  }

  return (
    <SyncoTooltip label="Group messages not sent to Threads Inbox">
      <CircularIconButton
        size="sm"
        variant="ghost"
        onClick={onToggleDistributionGroupNotificationsClicked}
        icon={<InboxSlashIcon boxSize={5} fill={colors.gray30} />}
        aria-label="Toggle Inbox"
      />
    </SyncoTooltip>
  );
};

export default memo(ToggleDistributionGroupInboxButton);
